import { ApiError, apiRequest } from './core/client';

// ============================================================================
// Order prep — the DRAFT ORDER the daily order-prep run maintains for every
// to-be-ordered journey. A draft is PREPARATION, never one of the four order
// documents: it proposes quantities, it is never written to order_lines, and
// placing the order stays a human act. One draft per journey; the run closes
// it when the journey advances past `announced`.
// ============================================================================

export type DraftMode = 'blind' | 'preorder' | 'hybrid';
export type DraftStatus = 'building' | 'active' | 'final' | 'closed';
export type DraftLineKind = 'release' | 'restock';

/** Read-time aggregate for one kind's lines — the server never stores totals. */
export interface DraftKindCounts {
  lines: number;
  /** Lines with no qty yet (null qty; a deliberate skip is qty 0, not unsized). */
  unsized: number;
  /** Lines the review agent flagged as a question for Matt. */
  flagged: number;
  units: number;
  estCost: number | null;
  /** True when any cost is estimated (brand ratio) or missing — show as "~". */
  costEstimated: boolean;
}

/** The inputs used at a line's LAST sizing. Daily history lives in signals. */
export interface DraftLineSignals {
  sold?: number | null;
  baskets?: number | null;
  notify_me?: number | null;
  available?: number | null;
  on_order?: number | null;
  as_of?: string | null;
  [key: string]: unknown;
}

export interface DraftLine {
  id: string;
  kind: DraftLineKind;
  lineKey: string;
  announcementLineId: string | null;
  pid: number | null;
  vendorSku: string | null;
  upc: string | null;
  itemnumber: string | null;
  description: string;
  /** Benchmark cohort label ("dies", "stamps", "ephemera"…). */
  productType: string | null;
  /** null = not yet sized; 0 = deliberate skip (rationale says why). */
  qty: number | null;
  /** 'matt' marks a manual override — it survives daily refreshes as-is. */
  qtySource: 'model' | 'matt';
  /** Order multiple (vendor "Inner") — qty must be a multiple of this. */
  packQty: number | null;
  minQty: number | null;
  unitCost: number | null;
  costBasis: 'vendor' | 'estimated' | null;
  /** One line of auditable arithmetic: "sold 14 + 2×9 baskets = 32 → 36 (mult 6)". */
  rationale: string | null;
  signals: DraftLineSignals | null;
  /** 'flag' = the review agent has a question; a qty change clears it server-side. */
  reviewFlag: 'ok' | 'flag' | null;
  reviewNote: string | null;
  position: number;
  updatedAt: string | null;
}

/** One cohort row of the cached blind-engine output. */
export interface BenchmarkTypeRow {
  product_type?: string;
  benchmark?: number | string;
  window?: string;
  cohort_n?: number;
  comparables?: unknown;
  note?: string;
  [key: string]: unknown;
}

export interface DraftBenchmark {
  types?: BenchmarkTypeRow[];
  designer?: string;
  computed_at?: string;
  [key: string]: unknown;
}

/** Draft-level review-agent verdict. Per-line flags live on the lines. */
export interface DraftReview {
  verdict?: 'ok' | 'flag';
  summary?: string;
  trigger?: 'first_sizing' | 'material_delta' | 'outlier' | 'due_window' | 'manual';
  reviewed_at?: string;
  [key: string]: unknown;
}

export interface DraftSummary {
  id: string;
  orderId: string;
  mode: DraftMode;
  status: DraftStatus;
  /** Building only: exactly what's missing (no pids yet / no cost basis / …). */
  blockedReason: string | null;
  benchmark: DraftBenchmark;
  review: DraftReview | null;
  /** Last sizing recompute — how fresh the numbers are (the run is daily). */
  refreshedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  /** Joined journey context, so the list renders without a second fetch. */
  journey?: {
    vendor: string;
    status: string;
    label: string | null;
    orderDueDate: string | null;
    expectedAvailability: string | null;
    archivedAt: string | null;
  };
  counts: Partial<Record<DraftLineKind, DraftKindCounts>>;
}

/**
 * A review lever: a named batch of line targets the review proposes, measured
 * by the server against the lines as they stand (server src/lib/draftLevers.js).
 * Today's "Apply the review's changes" applies the `default: 'apply'` ones over
 * the $50 floor whose lines all still exist.
 */
export interface DraftLever {
  key: string;
  label: string;
  /** What the review recommends: apply, or skip (optional). */
  default: 'apply' | 'skip';
  note?: string;
  lines: number;
  changes: Array<{ kind: DraftLineKind; line_key: string; qty: number }>;
  /** What applying moves (or, once applied, moved): units and known-cost dollars. */
  units: number;
  cost: number;
  unknown_cost_lines: number;
  /** "kind line_key" refs the draft no longer has — the lever cannot apply. */
  missing_lines: string[];
  over_floor: boolean;
  applied: { at: string; by: string | null } | null;
}

export interface DraftDetail extends DraftSummary {
  /** Server-ordered: kind, then position. */
  lines: DraftLine[];
  levers: DraftLever[];
}

/** Apply (or revert) review levers by key; revert restores what the lines held. */
export async function setDraftLevers(orderId: string, keys: string[], apply: boolean): Promise<void> {
  await apiRequest({
    path: `/order-prep/drafts/${encodeURIComponent(orderId)}/levers`,
    method: 'POST',
    body: { keys, apply },
  });
}

/** One day's preorder-signal snapshot for one product on one journey. */
export interface DraftSignalRow {
  orderId: string;
  pid: number;
  snapshotDate: string | null;
  sold: number | null;
  baskets: number | null;
  notifyMe: number | null;
  available: number | null;
  onOrder: number | null;
}

/** Every open (non-closed) draft, due-date first — joins ToOrderView by orderId. */
export async function getDraftOrders(): Promise<DraftSummary[]> {
  const response = await apiRequest<{ drafts: DraftSummary[] }>({
    path: '/order-prep/drafts',
    method: 'GET',
  });
  return Array.isArray(response.drafts) ? response.drafts : [];
}

/** One journey's draft with lines. null = the run has not opened one yet. */
export async function getDraftOrder(orderId: string): Promise<DraftDetail | null> {
  try {
    const response = await apiRequest<{ draft: DraftDetail }>({
      path: `/order-prep/drafts/${encodeURIComponent(orderId)}`,
      method: 'GET',
    });
    return response.draft ?? null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** Daily signal history for a journey's lines — sparkline fuel. */
export async function getDraftSignals(orderId: string): Promise<DraftSignalRow[]> {
  const response = await apiRequest<{ signals: DraftSignalRow[] }>({
    path: '/order-prep/signals',
    method: 'GET',
    query: { order_id: orderId },
  });
  return Array.isArray(response.signals) ? response.signals : [];
}

/**
 * Matt's qty override on one line — the narrow write it actually is: line id
 * and the new qty. (It used to round-trip the WHOLE line through the PUT
 * upsert, where a stale client copy clobbered cost/rationale/signals the
 * daily refresh had just recomputed.) The server sets qty_source 'matt' so
 * the override survives refreshes, and clears any review flag — a changed
 * number is unreviewed by definition.
 */
export async function overrideDraftLineQty(
  orderId: string,
  lineId: string,
  qty: number
): Promise<void> {
  await apiRequest({
    path: `/order-prep/drafts/${encodeURIComponent(orderId)}/line-qty`,
    method: 'POST',
    body: { line_id: lineId, qty },
  });
}

// ============================================================================
// Setup folders — the synced mirror of the setup-pending workspace. One row
// per journey folder: its setup state plus the built products.json payload,
// so ready-vs-waiting (and the deliverable itself) renders without Matt's
// machine. The filesystem stays the working home; syncedAt is the honesty flag.
// ============================================================================

/** Same vocabulary as setup_folders_status_check (orders-server/db/schema.sql). */
export type SetupFolderStatus =
  | 'in_progress' | 'awaiting_assets' | 'ready' | 'submitted' | 'mapping_only' | 'parked' | 'archived';

export interface SetupFolderStaging {
  staged?: boolean;
  expired?: boolean;
  last_staged_at_utc?: string;
  expired_at_utc?: string;
  images?: number;
  note?: string;
  [key: string]: unknown;
}

/** The free-form setup state — promoted fields also exist as columns on the row.
 *  `staging` is always the object form: the sync canonicalises legacy string
 *  values at ingestion; stored rows were normalized by
 *  20260820_setup_staging_normalize.sql. */
export interface SetupFolderState {
  waiting_on?: string | null;
  blocked_on?: string[];
  staging?: SetupFolderStaging | null;
  upcs_generated?: boolean;
  notes?: string | null;
  [key: string]: unknown;
}

export interface SetupFolderSummary {
  folder: string;
  orderId: string | null;
  vendor: string | null;
  status: SetupFolderStatus | null;
  /** The folder's product count (what the folder claims is built). */
  products: number | null;
  /** Vendor release embargo — don't LIST before. */
  embargo: string | null;
  /** Don't SUBMIT before (e.g. UC-style windows). */
  doNotSubmitBefore: string | null;
  lastValidated: string | null;
  state: SetupFolderState;
  /** Payload metadata only — the array itself rides the detail fetch. */
  hasPayload: boolean;
  payloadProducts: number | null;
  payloadUpdatedAt: string | null;
  syncedAt: string | null;
  updatedAt: string | null;
  /** Setup Preview submit marker (inventory app) — setup-state writes never clear it. */
  submittedAt?: string | null;
  submittedBy?: string | null;
  submissionCount?: number;
  lastSubmission?: SetupSubmission | null;
}

/** One Setup Preview submit, as recorded by POST /order-prep/setup-state/:folder/submitted. */
export interface SetupSubmission {
  at: string;
  by: string | null;
  success: boolean;
  shown: boolean;
  product_count: number;
  created_count: number;
  errored_count: number;
  query_id: string | null;
  product_tool_url: string | null;
  payload_hash: string | null;
}

export interface SetupFolderDetail extends SetupFolderSummary {
  /** The folder's products.json verbatim — ready to copy into the importer. */
  payload: Record<string, unknown>[] | null;
}

/** All non-archived folders — joins SetupView by orderId. */
/** The default list leaves parked and archived folders out; `status` asks for one status instead. */
export async function getSetupFolders(opts: { status?: SetupFolderStatus } = {}): Promise<SetupFolderSummary[]> {
  const response = await apiRequest<{ folders: SetupFolderSummary[] }>({
    path: '/order-prep/setup-state',
    method: 'GET',
    ...(opts.status ? { query: { status: opts.status } } : {}),
  });
  return Array.isArray(response.folders) ? response.folders : [];
}

/** One folder with its full products.json payload (the copy fetch). */
export async function getSetupFolder(folder: string): Promise<SetupFolderDetail | null> {
  try {
    const response = await apiRequest<{ folder: SetupFolderDetail }>({
      path: `/order-prep/setup-state/${encodeURIComponent(folder)}`,
      method: 'GET',
    });
    return response.folder ?? null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Answer a review flag without changing the number: 'ok' = "the qty stands"
 * (the reviewer's question is settled), null clears the flag entirely.
 */
export async function setDraftLineReview(
  orderId: string,
  line: DraftLine,
  flag: 'ok' | 'flag' | null,
  note?: string | null
): Promise<void> {
  await apiRequest({
    path: `/order-prep/drafts/${encodeURIComponent(orderId)}/review`,
    method: 'POST',
    body: {
      line_reviews: [{
        id: line.id,
        kind: line.kind,
        review_flag: flag,
        review_note: note ?? line.reviewNote,
      }],
    },
  });
}
